import { useMemo } from 'react';
import { notificationService } from '../services/notification.service';
import { useObservable } from '@/shared/hooks/useObservable';
import { StepOperator } from './StepOperator';
import { StepFlight } from './StepFlight';
import { StepMap } from './StepMap';
import { StepPreview } from './StepPreview';
import { Button } from '@/shared/components/Button';

const STEP_LABELS = [
  { sl: 'Operator', en: 'Operator' },
  { sl: 'Let', en: 'Flight' },
  { sl: 'Karta', en: 'Map' },
  { sl: 'Pregled', en: 'Preview' },
];

const STEP_SUBTITLES = [
  'Korak 1/4: Operator & UAS',
  'Korak 2/4: Podatki o letu',
  'Korak 3/4: Risanje na karti',
  'Korak 4/4: Pregled & generiranje',
];

export function StepperPanel() {
  const currentStep = useObservable(notificationService.currentStep$, 1);
  // Memoize so useObservable doesn't re-subscribe on every render
  const stepValidObs = useMemo(() => notificationService.getStepValid$(currentStep), [currentStep]);
  const stepValid = useObservable(stepValidObs, false);

  function goNext() {
    if (currentStep < 4) notificationService.setStep((currentStep + 1) as 1 | 2 | 3 | 4);
  }

  function goBack() {
    if (currentStep > 1) notificationService.setStep((currentStep - 1) as 1 | 2 | 3 | 4);
  }

  return (
    <div className="flex flex-col h-full bg-slate-800 border-l border-slate-700">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-700 flex-shrink-0">
        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">
          Najava leta / Flight notification
        </div>
        <div className="text-xs text-slate-600 mt-0.5">{STEP_SUBTITLES[currentStep - 1]}</div>
      </div>

      {/* Stepper dots */}
      <div className="px-5 pt-4 flex-shrink-0">
        <div className="flex items-center">
          {[1, 2, 3, 4].map((step, i) => (
            <div key={step} className="flex items-center flex-1 last:flex-none">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
                  ${step < currentStep ? 'bg-blue-700 text-white' : ''}
                  ${step === currentStep ? 'bg-blue-500 text-white ring-2 ring-blue-500/30' : ''}
                  ${step > currentStep ? 'bg-slate-700 text-slate-500' : ''}
                `}
              >
                {step}
              </div>
              {i < 3 && (
                <div className={`flex-1 h-0.5 mx-1 ${step < currentStep ? 'bg-blue-700' : 'bg-slate-700'}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex mt-1.5">
          {STEP_LABELS.map((label, i) => (
            <div
              key={i}
              className={`flex-1 text-center text-[10px] ${i + 1 === currentStep ? 'text-blue-400 font-semibold' : 'text-slate-600'}`}
            >
              {label.sl}
            </div>
          ))}
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {currentStep === 1 && <StepOperator />}
        {currentStep === 2 && <StepFlight />}
        {currentStep === 3 && <StepMap />}
        {currentStep === 4 && <StepPreview />}
      </div>

      {/* Footer navigation */}
      <div className="px-5 py-3 border-t border-slate-700 flex gap-2 flex-shrink-0">
        <Button
          variant="secondary"
          onClick={goBack}
          className={`flex-1 ${currentStep === 1 ? 'invisible' : ''}`}
        >
          ← Nazaj
        </Button>
        {currentStep < 4 && (
          <Button
            variant="primary"
            onClick={goNext}
            disabled={!stepValid}
            className="flex-[2]"
          >
            Naprej →
          </Button>
        )}
      </div>
    </div>
  );
}
